import { type RefObject, useEffect, useRef, useState } from 'react'

/**
 * Distance à parcourir dans une direction avant d'y croire. Descendre coûte
 * plus cher que remonter : escamoter la barre par erreur fait perdre l'accès à
 * la recherche et aux filtres, la faire réapparaître par erreur ne coûte qu'un
 * bandeau. Remplace un seuil de 8 px, trop court pour distinguer un geste d'un
 * rebond élastique iOS.
 */
const HIDE_RUN_PX = 64
const SHOW_RUN_PX = 32

/**
 * `true` quand l'utilisateur descend la page : la barre collante des résultats
 * s'escamote alors et réapparaît dès qu'il remonte.
 *
 * `ref` pointe l'élément collant. Sa position dans le flux (mesurée à 178 px sur la
 * page résultats en 375 px) sert de zone haute : en dessous, la
 * barre reste toujours visible. Un seuil en dur valait 80 px, soit 90 px de
 * moins que cette position — la barre s'escamotait avant même de s'être collée,
 * elle disparaissait d'un endroit où elle ne gênait personne. Le nombre dépend
 * de l'en-tête au-dessus (bandeau téléphone, fil d'étapes, résumé de recherche
 * qui passe à la ligne), donc il se mesure au lieu de se deviner.
 *
 * Volontairement séparé de {@link useScrollInfo}, qui pose un état à CHAQUE
 * événement de scroll : ici l'état ne change qu'aux inversions de direction,
 * donc le composant collant ne se re-rend pas à chaque pixel.
 */
export function useHideOnScrollDown(ref: RefObject<HTMLElement | null>): boolean {
  const [hidden, setHidden] = useState(false)
  const lastScrollY = useRef(0)
  /** Distance cumulée dans la direction courante, remise à zéro à chaque inversion. */
  const run = useRef(0)

  useEffect(() => {
    lastScrollY.current = window.scrollY
    // Position de la barre dans le document. `offsetTop` est relatif au parent
    // positionné le plus proche, et le cumul le long de la chaîne surcompte
    // (208 mesuré contre 178 réels sur la page résultats) : le rectangle plus
    // le défilement courant est la seule mesure juste par définition. Lue une
    // fois — la position dans le flux ne bouge pas quand l'élément se colle, et
    // la relire à chaque scroll forcerait un calcul de layout par événement.
    const topZone = ref.current ? ref.current.getBoundingClientRect().top + window.scrollY : 0

    const handleScroll = () => {
      const currentY = window.scrollY
      const delta = currentY - lastScrollY.current
      lastScrollY.current = currentY

      if (currentY <= topZone) {
        run.current = 0
        setHidden(false)
        return
      }

      if (delta === 0) return
      if (Math.sign(delta) !== Math.sign(run.current)) run.current = 0
      run.current += delta

      if (run.current >= HIDE_RUN_PX) setHidden(true)
      else if (run.current <= -SHOW_RUN_PX) setHidden(false)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [ref])

  return hidden
}
